/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { ExpandableTable } from "../../../components/UniversalTable";
import { documentTypeTableColumns } from "../../../helpers";
import { KeyValue } from "views/applications-id-report/components/KeyValue";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { W2Item } from "../../../types/verified/w2";
import { DocumentCard } from "views/applications-id-report/slices/IncomeReportSlice/components/DocumentCard";
import { useMemo } from "react";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import { WarningRecommendation } from "./WarningRecommendation";
import { Show } from "shared/components";

export const W2Table = ({ incomeItems }: { incomeItems?: W2Item[] }) => {
  const { flatW2s, flatWarnings } = useMemo(
    () => ({
      flatW2s:
        incomeItems?.flatMap((i) =>
          i.data.document.income_sources.flatMap((incSource) => incSource.forms)
        ) ?? [],
      flatWarnings: incomeItems?.flatMap((i) => i.data.document.warnings) ?? []
    }),
    [incomeItems]
  );

  return (
    <ExpandableTable
      cellClassName="p-4 pl-0 text-sm self-center"
      headerRowClassName="text-content-300"
      renderExpanded={(form) => {
        const isWarningEntity = "doc_info" in form;
        const formAnalysis = isWarningEntity
          ? form?.form_analysis
          : form?.doc_analysis?.form_analysis;
        const docScore = formAnalysis?.[0]?.form_authenticity?.score,
          reasonCodes = formAnalysis?.[0]?.form_authenticity?.reason_codes;

        const status = parseDocStatus(docScore);
        const rejectionReason = isWarningEntity
          ? (form?.doc_info?.rejection_reason ?? reasonCodes?.[0]?.description)
          : reasonCodes?.[0]?.description;
        const hasAsset = Boolean(form?.asset?.asset);

        return (
          <div>
            <div className="px-6 py-8 bg-background-900">
              <div className="mt-6 mb-4">
                <WarningRecommendation
                  status={status}
                  rejectionReason={rejectionReason}
                />

                <Show if={hasAsset}>
                  <div className="h-[1px] w-full bg-alpha-40 my-3" />
                  <DocumentCard
                    score={docScore}
                    asset={form?.asset?.asset}
                    reason_codes={reasonCodes}
                    ignorePrint
                  />
                </Show>
              </div>

              {isWarningEntity ? null : (
                <>
                  <ContentTitle
                    title="Compensation and tax information"
                    contentClassName="space-y-2"
                  >
                    {/*
                      Missing

                      Wages tips other comp
                      Website
                      Group application
                    */}

                    <KeyValue label="Year" value={form?.year} />
                    <KeyValue
                      label="Address"
                      value={form?.fields?.employer_address_line1?.value || "-"}
                    />
                    <KeyValue
                      label="Social security wages"
                      value={form?.fields?.social_security_wages?.value || "-"}
                    />

                    <KeyValue
                      label="Medicare wages and tips"
                      value={
                        form?.fields?.medicare_wages_and_tips?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Medicare tax withheld"
                      value={form?.fields?.medicare_tax_withheld?.value || "-"}
                    />

                    <KeyValue
                      label="Social security tax withheld"
                      value={
                        form?.fields?.social_security_tax_withheld?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Social security tips"
                      value={form?.fields?.social_security_tips?.value || "-"}
                    />

                    <KeyValue
                      label="Allocated tips"
                      value={form?.fields?.allocated_tips?.value || "-"}
                    />

                    <KeyValue
                      label="Federal income tax withheld"
                      value={
                        form?.fields?.federal_income_tax_withheld?.value || "-"
                      }
                    />

                    <KeyValue
                      label="State income tax primary"
                      value={
                        form?.fields?.state_income_tax_primary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Local wages tips primary"
                      value={
                        form?.fields?.local_wages_tips_primary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Locality name primary"
                      value={form?.fields?.locality_name_primary?.value || "-"}
                    />

                    <KeyValue
                      label="Local wages tips secondary"
                      value={
                        form?.fields?.local_wages_tips_secondary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="Locality name secondary"
                      value={
                        form?.fields?.locality_name_secondary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="State wages tips primary"
                      value={
                        form?.fields?.state_wages_tips_primary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="State wages tips secondary"
                      value={
                        form?.fields?.state_wages_tips_secondary?.value || "-"
                      }
                    />

                    <KeyValue
                      label="State income tax secondary"
                      value={
                        form?.fields?.state_income_tax_secondary?.value || "-"
                      }
                    />
                  </ContentTitle>

                  <ContentTitle
                    title="Employer information"
                    contentClassName="space-y-2"
                  >
                    <KeyValue
                      label="Employer ID no."
                      value={form?.fields?.employer_id_no?.value || "-"}
                    />

                    <KeyValue
                      label="Employer name"
                      value={form?.fields?.employer_name?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address state"
                      value={form?.fields?.employer_address_state?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address city"
                      value={form?.fields?.employer_address_city?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address zip"
                      value={form?.fields?.employer_address_zip?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address line1"
                      value={form?.fields?.employer_address_line1?.value || "-"}
                    />

                    <KeyValue
                      label="Employer address line2"
                      value={form?.fields?.employer_address_line2?.value || "-"}
                    />

                    <KeyValue
                      label="Employer state no. primary"
                      value={form?.fields?.state_primary?.value || "-"}
                    />
                  </ContentTitle>

                  <ContentTitle
                    title="Employee information"
                    contentClassName="space-y-2"
                  >
                    <KeyValue
                      label="Employee name"
                      value={form?.fields?.employee_name?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address state"
                      value={form?.fields?.employee_address_state?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address city"
                      value={form?.fields?.employee_address_city?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address line1"
                      value={form?.fields?.employee_address_line1?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address line2"
                      value={form?.fields?.employee_address_line2?.value || "-"}
                    />

                    <KeyValue
                      label="Employee address zip"
                      value={form?.fields?.employee_address_zip?.value || "-"}
                    />

                    <KeyValue
                      label="Statutory employee"
                      value={form?.fields?.statutory_employee?.value || "-"}
                    />
                  </ContentTitle>

                  <ContentTitle
                    title="Additional information"
                    contentClassName="space-y-2"
                  >
                    <KeyValue
                      label="Box9"
                      value={form?.fields?.box9?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 a code"
                      value={form?.fields?.box12a_code?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 b code"
                      value={form?.fields?.box12b_code?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 c code"
                      value={form?.fields?.box12c_code?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 d code"
                      value={form?.fields?.box12dcode?.value || "-"}
                    />

                    <KeyValue
                      label="Box14 other"
                      value={form?.fields?.box14_other?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 a amount"
                      value={form?.fields?.box12a_amount?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 b amount"
                      value={form?.fields?.box12b_amount?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 c amount"
                      value={form?.fields?.box12c_amount?.value || "-"}
                    />

                    <KeyValue
                      label="Box12 d amount"
                      value={form?.fields?.box12d_amount?.value || "-"}
                    />

                    <KeyValue
                      label="Qualified plans"
                      value={form?.fields?.qualified_plans?.value || "-"}
                    />

                    <KeyValue
                      label="Retirement plan"
                      value={form?.fields?.retirement_plan?.value || "-"}
                    />

                    <KeyValue
                      label="State primary"
                      value={form?.fields?.state_primary?.value || "-"}
                    />

                    <KeyValue
                      label="State secondary"
                      value={form?.fields?.state_secondary?.value || "-"}
                    />

                    <KeyValue
                      label="Third-party sick pay"
                      value={form?.fields?.third_party_sick_pay?.value || "-"}
                    />
                    <KeyValue
                      label="Locality name primary"
                      value={form?.fields?.locality_name_primary?.value || "-"}
                    />

                    <KeyValue
                      label="Dependent care benefits"
                      value={
                        form?.fields?.dependent_care_benefits?.value || "-"
                      }
                    />
                  </ContentTitle>
                </>
              )}
            </div>
          </div>
        );
      }}
      rows={[...flatWarnings, ...flatW2s]}
      columns={documentTypeTableColumns}
    />
  );
};
